/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Curated career-journey beats for the about-page 3D hero.
 * Facts stay in linkedin-profile.json; this file maps stages, logos, and art slots.
 */

export type Vec3 = [number, number, number];

export type LogoTile = 'white' | 'none' | 'dark' | 'round';

export type LandmarkKind = 'arch' | 'tower' | 'skyline' | 'block';

export type LandmarkSlot = {
  id: string;
  /** Drop a GLB here; until it exists the scene draws a labeled placeholder. */
  src: string;
  position: Vec3;
  rotation?: Vec3;
  scale?: number;
  kind: LandmarkKind;
  label: string;
};

export type Atmosphere = {
  fog: string;
  ambient: string;
  sun: string;
  ground: string;
  skyTop: string;
  skyBottom: string;
};

export type JourneyChapter = {
  id: string;
  label: string;
  subtitle: string;
  location: string;
  years: string;
  logo?: string;
  /** Prebuilt extruded mark from `one-square-mile logos`. */
  logoGlb?: string;
  /** Brand ink from the chapter logo, used by the portal title that leads here. */
  logoColor?: string;
  tile: LogoTile;
  experienceIds: string[];
  /** Stage origin in world space. */
  position: Vec3;
  landmarks: LandmarkSlot[];
  atmosphere: Atmosphere;
  /** OSMnx street-print PNG from modules/one-square-mile. */
  backdrop?: string;
  /** Street-print plane edge length. Default STREET_PRINT_SIZE (36). */
  mapSize?: number;
  /** Hidden beat: camera flies here, but chips and portal titles stay unnamed. */
  tease?: boolean;
};

/** Street-print plane size. Stages sit farther apart than this so maps do not overlap. */
export const STREET_PRINT_SIZE = 36;
export const STAGE_GROUND_DEPTH = 40;
/** Center-to-center spacing: ground depth plus a dark corridor for a portal gate. */
export const STAGE_GAP = 64;

/**
 * GLB is authored in meters. Scene scale is printSize / (2 * distMeters)
 * so the landmark sits on the OSMnx crop. PNG-up is scene -Z (true north
 * only when the place's up_heading is 0).
 */

export const PORTAL = {
  // Small street-height door (center ≈ FLY_EYE_Y). The fog wall behind it
  // must exceed horizontal FOV at rest (~46 m, wide canvas).
  doorWidth: 7.2,
  doorHeight: 6.4,
  wallWidth: 900,
  wallHeight: 240,
  thickness: 2.4,
} as const;

/** Chapter logos sit on the roadway, above the asphalt. */
export const STREET_OVERLAY: Vec3 = [0, 3.4, -14];

/** Street-level eye height for every rest pose and portal pass. */
export const FLY_EYE_Y = 3.2;

/**
 * FAST / GOOD / WELL sits on the centerline just ahead of the spawn camera
 * so the opening shot is a hero, not a distant sign.
 * Camera rest is stage z+14; this is ~8 m in front of it.
 */
export const THESIS_LOCKUP: Vec3 = [0, FLY_EYE_Y, 6];

function stageZ(index: number): number {
  return -index * STAGE_GAP;
}

/** World Z of the gate between chapter `fromIndex` and the next. */
export function portalZBetween(fromIndex: number): number {
  return stageZ(fromIndex) - STAGE_GAP / 2;
}

export const ABOUT_JOURNEY: JourneyChapter[] = [
  {
    id: 'thesis',
    label: 'Product Engineering',
    subtitle: 'Fast · Good · Well',
    location: 'Greenpoint',
    years: '',
    tile: 'none',
    experienceIds: [],
    position: [0, 0, stageZ(0)],
    landmarks: [
      {
        id: 'greenpoint-richardson',
        src: '/about-journey/landmarks/greenpoint-richardson.glb',
        // 90 m crop, one-way Richardson eastbound; 214 ahead-right.
        position: [0, 0, 0],
        scale: 0.22222,
        kind: 'block',
        label: '214 Richardson',
      },
    ],
    backdrop: '/about-journey/backdrops/thesis.png',
    mapSize: 40,
    atmosphere: {
      fog: '#0f172a',
      ambient: '#1e3a8a',
      sun: '#fbbf24',
      ground: '#1e293b',
      skyTop: '#0f172a',
      skyBottom: '#1e3a8a',
    },
  },
  {
    id: 'georgia-tech',
    label: 'Georgia Tech',
    subtitle: 'BS Computer Science · GTRI · Jazz · Lacrosse',
    location: 'Atlanta',
    years: '2012 – 2017',
    logo: '/linkedin-banner/logos/georgia-tech.svg',
    logoGlb: '/about-journey/logos/georgia-tech.glb',
    logoColor: '#b3a369',
    tile: 'white',
    experienceIds: [
      'exp-georgia-tech-research-institute-software-engineering-researcher',
      'exp-georgia-institute-of-technology-teaching-assistant',
    ],
    position: [0, 0, stageZ(1)],
    landmarks: [
      {
        id: 'atlanta-tech-tower',
        src: '/about-journey/landmarks/atlanta-tech-tower.glb',
        // 180 m crop yawed 315°: tower at 10:30, south facade toward camera.
        position: [-6.285, 0, -6.285],
        rotation: [0, -Math.PI / 4, 0],
        scale: 0.11111,
        kind: 'tower',
        label: 'Tech Tower',
      },
    ],
    backdrop: '/about-journey/backdrops/georgia-tech.png',
    mapSize: 40,
    atmosphere: {
      fog: '#6b8aa8',
      ambient: '#94a3b8',
      sun: '#fde68a',
      ground: '#78716c',
      skyTop: '#7dd3fc',
      skyBottom: '#e2e8f0',
    },
  },
  {
    id: 'barcelona',
    label: 'Barcelona',
    subtitle: 'Founding Engineer @ Glovo · HackGT@UPC',
    location: 'Barcelona',
    years: '2015 – 2016',
    logo: '/linkedin-banner/logos/glovo.svg',
    logoGlb: '/about-journey/logos/barcelona.glb',
    logoColor: '#00a082',
    tile: 'white',
    experienceIds: [
      'exp-glovo-founding-engineer',
      'exp-georgia-institute-of-technology-teaching-assistant',
    ],
    position: [0, 0, stageZ(2)],
    landmarks: [
      {
        id: 'barcelona-arc-de-triomf',
        src: '/about-journey/landmarks/barcelona-arc-de-triomf.glb',
        // 70 m crop yawed to the OSM Passeig spine; opening along the fly-through.
        position: [0, 0, 0],
        scale: 0.28571,
        kind: 'arch',
        label: 'Arc de Triomf',
      },
    ],
    backdrop: '/about-journey/backdrops/barcelona.png',
    mapSize: 40,
    atmosphere: {
      fog: '#f4a261',
      ambient: '#fdba74',
      sun: '#fb923c',
      ground: '#c2410c',
      skyTop: '#fb7185',
      skyBottom: '#fed7aa',
    },
  },
  {
    id: 'fixd',
    label: 'FIXD',
    subtitle: 'Founding Engineer',
    location: 'Atlanta',
    years: '2017 – 2018',
    logo: '/linkedin-banner/logos/fixd.png',
    logoGlb: '/about-journey/logos/fixd.glb',
    logoColor: '#22c55e',
    tile: 'white',
    experienceIds: ['exp-fixd-automotive-incdot-founding-engineer'],
    position: [0, 0, stageZ(3)],
    landmarks: [
      {
        id: 'atlanta-industrial',
        src: '/about-journey/landmarks/atlanta-industrial.glb',
        position: [5, 0, -5],
        kind: 'block',
        label: 'Atlanta',
      },
    ],
    backdrop: '/about-journey/backdrops/fixd.png',
    atmosphere: {
      fog: '#64748b',
      ambient: '#94a3b8',
      sun: '#e2e8f0',
      ground: '#44403c',
      skyTop: '#64748b',
      skyBottom: '#cbd5e1',
    },
  },
  {
    id: 'amplify',
    label: 'Amplify',
    subtitle: 'Senior Software Engineer · Grand Central',
    location: 'DUMBO, Brooklyn',
    years: '2018 – 2020',
    logo: '/linkedin-banner/logos/amplify-icon.png',
    logoGlb: '/about-journey/logos/amplify.glb',
    logoColor: '#f15a22',
    tile: 'none',
    experienceIds: ['exp-amplify-senior-software-engineer'],
    position: [0, 0, stageZ(4)],
    landmarks: [
      {
        id: 'dumbo-washington',
        src: '/about-journey/landmarks/dumbo-washington.glb',
        // 220 m crop yawed to Washington Street northbound.
        position: [0, 0, 0],
        scale: 0.09091,
        kind: 'block',
        label: 'DUMBO',
      },
    ],
    backdrop: '/about-journey/backdrops/amplify.png',
    mapSize: 40,
    atmosphere: {
      fog: '#94a3b8',
      ambient: '#cbd5e1',
      sun: '#fde68a',
      ground: '#78716c',
      skyTop: '#7dd3fc',
      skyBottom: '#e2e8f0',
    },
  },
  {
    id: 'bees',
    label: 'BEES',
    subtitle: 'Software Architect · AB InBev',
    location: 'Manhattan',
    years: '2020 – 2022',
    logo: '/linkedin-banner/logos/bees.svg',
    logoGlb: '/about-journey/logos/bees.glb',
    logoColor: '#e8e8e8',
    tile: 'white',
    experienceIds: [
      'exp-bees-software-architect',
      'exp-anheuser-busch-inbev-principal-software-engineer',
    ],
    position: [0, 0, stageZ(5)],
    landmarks: [
      {
        id: 'nyc-manhattan',
        src: '/about-journey/landmarks/nyc-manhattan.glb',
        position: [-4, 0, -8],
        kind: 'skyline',
        label: 'Manhattan',
      },
    ],
    backdrop: '/about-journey/backdrops/bees.png',
    atmosphere: {
      fog: '#111827',
      ambient: '#1e293b',
      sun: '#f59e0b',
      ground: '#0f172a',
      skyTop: '#020617',
      skyBottom: '#1e3a8a',
    },
  },
  {
    id: 'triple-axle',
    label: 'Triple Axle',
    subtitle: 'Co-Founder · CTO then CEO',
    location: 'New York',
    years: '2022 – 2024',
    logo: '/linkedin-banner/logos/triple-axle.png',
    logoGlb: '/about-journey/logos/triple-axle.glb',
    logoColor: '#22b8e6',
    tile: 'dark',
    experienceIds: [
      'exp-triple-axle-co-founder-and-cto',
      'exp-triple-axle-co-founder-and-ceo',
    ],
    position: [0, 0, stageZ(6)],
    landmarks: [
      {
        id: 'nyc-brooklyn',
        src: '/about-journey/landmarks/nyc-brooklyn.glb',
        position: [6, 0, -6],
        kind: 'block',
        label: 'Brooklyn',
      },
    ],
    backdrop: '/about-journey/backdrops/triple-axle.png',
    atmosphere: {
      fog: '#1e293b',
      ambient: '#334155',
      sun: '#fbbf24',
      ground: '#27272a',
      skyTop: '#0c1222',
      skyBottom: '#334155',
    },
  },
  {
    id: 'slabstack',
    label: 'Slabstack',
    subtitle: 'CTO · VP of Engineering · Sysdyne',
    location: 'Brooklyn',
    years: '2024 – now',
    logo: '/linkedin-banner/logos/slabstack-sysdyne.png',
    logoGlb: '/about-journey/logos/slabstack.glb',
    logoColor: '#f5c400',
    tile: 'none',
    experienceIds: [
      'exp-slabstack-a-sysdyne-company-vice-president-of-engineering',
      'exp-slabstack-chief-technology-officer',
    ],
    position: [0, 0, stageZ(7)],
    landmarks: [
      {
        id: 'nyc-now',
        src: '/about-journey/landmarks/nyc-now.glb',
        position: [0, 0, -8],
        kind: 'skyline',
        label: 'Now',
      },
    ],
    backdrop: '/about-journey/backdrops/slabstack.png',
    atmosphere: {
      fog: '#122560',
      ambient: '#1e3a8a',
      sun: '#fbbf24',
      ground: '#1e293b',
      skyTop: '#020617',
      skyBottom: '#1e40af',
    },
  },
  {
    id: 'beyond',
    label: '',
    subtitle: '',
    location: '',
    years: '',
    tile: 'none',
    tease: true,
    experienceIds: [],
    position: [0, 0, stageZ(8)],
    landmarks: [],
    logoGlb: '/about-journey/logos/question-mark.glb',
    atmosphere: {
      fog: '#1c1008',
      ambient: '#3f2a14',
      sun: '#f59e0b',
      ground: '#030712',
      skyTop: '#000000',
      skyBottom: '#1c1008',
    },
  },
];

export function chapterIndexForProgress(progress: number): number {
  const last = ABOUT_JOURNEY.length - 1;
  if (last <= 0) return 0;
  return Math.min(last, Math.max(0, Math.round(progress * last)));
}

export function cameraPositionForChapter(chapter: JourneyChapter): Vec3 {
  const [x, y, z] = chapter.position;
  return [x, y + FLY_EYE_Y, z + 14];
}

export function cameraLookForChapter(chapter: JourneyChapter): Vec3 {
  const [x, y, z] = chapter.position;
  return [x, y + FLY_EYE_Y, z - 18];
}

function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Camera path: rest poses plus a pass through each city-gate title. */
export function cameraPathPoints(): Vec3[] {
  const pts: Vec3[] = [];
  ABOUT_JOURNEY.forEach((chapter, index) => {
    pts.push(cameraPositionForChapter(chapter));
    if (index < ABOUT_JOURNEY.length - 1) {
      const z = portalZBetween(index);
      pts.push([0, FLY_EYE_Y, z + 1.55]);
      pts.push([0, FLY_EYE_Y, z - 1.55]);
    }
  });
  return pts;
}

/**
 * Linear sample of `cameraPathPoints`. Catmull-Rom sagged below eye height
 * at every rest (portal 4.2 → rest 3.2 → portal 4.2) and looked like a dip.
 */
export function sampleCameraPath(t: number): Vec3 {
  const pts = cameraPathPoints();
  const clamped = Math.min(1, Math.max(0, t));
  const last = pts.length - 1;
  if (last <= 0) return pts[0] ?? [0, FLY_EYE_Y, 0];
  const scaled = clamped * last;
  const i = Math.min(last - 1, Math.floor(scaled));
  return lerpVec3(pts[i], pts[i + 1], scaled - i);
}
